import React, { useState, useEffect } from 'react';
import { paceDataAPI } from '../services/api';
import './BulkPaceStats.css';

function BulkPaceStats({ member }) {
  const [bulkData, setBulkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (member?.ghin) {
      loadBulkPaceData();
    }
  }, [member?.ghin]);

  const loadBulkPaceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paceDataAPI.getMemberBulkPaceData(member.ghin);
      
      if (response.data.success && response.data.data) {
        setBulkData(response.data.data);
      } else {
        setBulkData(null);
      }
    } catch (err) {
      console.error('Error loading bulk pace data:', err);
      setError(err.response?.data?.error || 'Failed to load bulk pace data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatBehindTime = (minutes) => {
    if (!minutes && minutes !== 0) return '0 min';
    return `${Math.round(minutes)} min`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getPaceRatingBadgeClass = (rating) => {
    const baseClass = 'pace-rating-badge';
    switch (rating) {
      case 'fast': return `${baseClass} fast`;
      case 'average': return `${baseClass} average`;
      case 'slow': return `${baseClass} slow`;
      case 'very-slow': return `${baseClass} very-slow`;
      default: return `${baseClass} unknown`;
    }
  };

  const getPaceRatingLabel = (rating) => {
    switch (rating) {
      case 'fast': return 'Fast';
      case 'average': return 'Average';
      case 'slow': return 'Slow';
      case 'very-slow': return 'Very Slow';
      default: return 'Unknown';
    }
  };

  const getRankSuffix = (rank) => {
    if (!rank) return '';
    const lastDigit = rank % 10;
    const lastTwoDigits = rank % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }
    
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  if (loading) {
    return (
      <div className="bulk-pace-stats loading">
        <div className="loading-message">
          <div className="spinner-small"></div>
          Loading bulk pace data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bulk-pace-stats error">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadBulkPaceData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!bulkData) {
    return (
      <div className="bulk-pace-stats no-data">
        <div className="no-data-message">
          <p>No bulk pace data available for this member.</p>
          <p className="sub-message">Bulk pace data may need to be synced from the pace management system.</p>
        </div>
      </div>
    );
  }

  const stats = bulkData.paceStats;

  return (
    <div className="bulk-pace-stats">
      <div className="stats-header">
        <h4>Bulk Pace Analytics</h4>
        <div className="sync-info">
          <span className="sync-source">{bulkData.syncSource} sync</span>
          <span className="last-synced">Last: {formatDate(bulkData.lastSynced)}</span>
        </div>
      </div>

      <div className="stats-grid">
        {/* Primary KPI: Behind Metrics */}
        <div className="stat-card primary">
          <div className="stat-header">
            <span className="stat-label">Average Behind</span>
            <span className="stat-badge primary">Primary KPI</span>
          </div>
          <div className="stat-value">{formatBehindTime(stats.averageBehind)}</div>
          {stats.behindRank && (
            <div className="stat-sub">
              Rank: {stats.behindRank}{getRankSuffix(stats.behindRank)}
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-label">Total Rounds</div>
          <div className="stat-value">{stats.totalRounds || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Average Round Time</div>
          <div className="stat-value">{formatTime(stats.averageRoundTime)}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Pace Rating</div>
          <div className="stat-value">
            <span className={getPaceRatingBadgeClass(stats.paceRating)}>
              {getPaceRatingLabel(stats.paceRating)}
            </span>
          </div>
        </div>
      </div>

      {/* Behind Time Distribution */}
      {stats.behindCounts && (
        <div className="behind-distribution">
          <h5>Behind Time Distribution</h5>
          <div className="distribution-bars">
            <div className="distribution-item">
              <div className="bar-label">Fast (0-8 min)</div>
              <div className="bar-container">
                <div
                  className="bar fast"
                  style={{ width: `${(stats.behindCounts.fast / stats.totalRounds) * 100}%` }}
                ></div>
                <span className="bar-count">{stats.behindCounts.fast}</span>
              </div>
            </div>
            <div className="distribution-item">
              <div className="bar-label">On Pace (9-15 min)</div>
              <div className="bar-container">
                <div
                  className="bar on-pace"
                  style={{ width: `${(stats.behindCounts.onPace / stats.totalRounds) * 100}%` }}
                ></div>
                <span className="bar-count">{stats.behindCounts.onPace}</span>
              </div>
            </div>
            <div className="distribution-item">
              <div className="bar-label">Behind (16-30 min)</div>
              <div className="bar-container">
                <div 
                  className="bar behind"
                  style={{ width: `${(stats.behindCounts.behind / stats.totalRounds) * 100}%` }}
                ></div>
                <span className="bar-count">{stats.behindCounts.behind}</span>
              </div>
            </div>
            <div className="distribution-item">
              <div className="bar-label">Far Behind (30+ min)</div>
              <div className="bar-container">
                <div 
                  className="bar far-behind"
                  style={{ width: `${(stats.behindCounts.farBehind / stats.totalRounds) * 100}%` }}
                ></div>
                <span className="bar-count">{stats.behindCounts.farBehind}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {stats.recentActivity && stats.recentActivity.length > 0 && (
        <div className="recent-activity">
          <h5>Recent Activity</h5>
          <div className="activity-list">
            {stats.recentActivity.slice(0, 3).map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-date">{formatDate(activity.date)}</div>
                <div className="activity-course">{activity.courseName}</div>
                <div className="activity-stats">
                  <span className="activity-behind">Behind: {formatBehindTime(activity.behindTime)}</span>
                  <span className="activity-time">Time: {formatTime(activity.roundTime)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.lastEventDate && (
        <div className="stats-footer">
          <span className="last-event">Last event: {formatDate(stats.lastEventDate)}</span>
        </div>
      )}
    </div>
  );
}

export default BulkPaceStats;